// src/Hooks/useUploadMailingListManage.js

import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, onSnapshot, doc, writeBatch } from 'firebase/firestore';
import { db } from '../Firebase/firebase';

export function useUploadMailingListManage() {
  const [activeAction, setActiveAction] = useState('VIEW');
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [focusedRowId, setFocusedRowId] = useState(null);
  const [dbRows, setDbRows] = useState([]);
  const [newRows, setNewRows] = useState([]);
  const [edits, setEdits] = useState({});
  const [regions, setRegions] = useState([]);

  // State to track all emails currently existing in the main mailingList
  const [mailingListEmails, setMailingListEmails] = useState(new Set());

  // Using a standard Object {} to guarantee React re-renders
  const [unsubscribedEmails, setUnsubscribedEmails] = useState({});

  // Dynamic Admin Limit State (Defaults to 100)
  const [importLimit, setImportLimit] = useState(100);

  // --- SORTING STATE ---
  const [sortConfig, setSortConfig] = useState({ key: 'userName', direction: 'asc' });

  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);
  const [showOverwriteAlert, setShowOverwriteAlert] = useState(false);

  const [showResultAlert, setShowResultAlert] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [resultSeverity, setResultSeverity] = useState('info');

  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);

  const [exportSettings, setExportSettings] = useState(() => {
    const saved = sessionStorage.getItem('uploadMailingListExportSettings');
    return saved ? JSON.parse(saved) : { selectedRegionIds: [], blockSize: 50 };
  });

  useEffect(() => {
    sessionStorage.setItem(
      'uploadMailingListExportSettings',
      JSON.stringify(exportSettings),
    );
  }, [exportSettings]);

  useEffect(() => {
    const unsubMailingList = onSnapshot(
      collection(db, 'uploadMailingList'),
      (snapshot) => {
        const fetchedData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setDbRows(fetchedData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching uploadMailingList (Check Firestore Rules):', error);
        setLoading(false);
      },
    );

    const unsubRegions = onSnapshot(
      collection(db, 'region'),
      (snapshot) => {
        let fetchedRegions = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        fetchedRegions = fetchedRegions.filter((r) => !r.isArchived);
        fetchedRegions.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setRegions(fetchedRegions);
      },
      (error) => {
        console.error('Error fetching regions:', error);
      },
    );

    // Real-time listener for the main mailingList to check cross-collection duplicates
    const unsubRealMailingList = onSnapshot(
      collection(db, 'mailingList'),
      (snapshot) => {
        const emails = new Set();
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.email) {
            emails.add(data.email.toLowerCase().trim());
          }
        });
        setMailingListEmails(emails);
      },
      (error) => {
        console.error('Error fetching real mailingList for duplicate check:', error);
      },
    );

    // Real-time listener uses a standard object
    const unsubUnsubscribeRequests = onSnapshot(
      collection(db, 'unsubscribeRequests'),
      (snapshot) => {
        const emailsObj = {};
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.email) {
            emailsObj[data.email.toLowerCase().trim()] = data.status || 'Unsubscribed';
          }
        });
        setUnsubscribedEmails(emailsObj);
      },
      (error) => {
        console.error('Error fetching unsubscribeRequests:', error);
      },
    );

    // Real-time listener for Dynamic Import Limit
    const unsubImportLimit = onSnapshot(
      doc(db, 'settings', 'ImportLimit'),
      (snapshot) => {
        if (snapshot.exists() && snapshot.data().limit) {
          setImportLimit(Number(snapshot.data().limit));
        } else {
          setImportLimit(100); // Failsafe default
        }
      },
      (error) => {
        console.error('Error fetching dynamic ImportLimit config:', error);
      },
    );

    return () => {
      unsubMailingList();
      unsubRegions();
      unsubRealMailingList();
      unsubUnsubscribeRequests();
      unsubImportLimit();
    };
  }, []);

  const displayRows = useMemo(() => {
    const mergedDbRows = dbRows.map((row) =>
      edits[row.id] ? { ...row, ...edits[row.id] } : row,
    );
    const newRowIds = new Set(newRows.map((r) => r.id));
    let combinedRows = [...newRows, ...mergedDbRows.filter((r) => !newRowIds.has(r.id))];

    if (sortConfig.key) {
      combinedRows.sort((a, b) => {
        let valA = a[sortConfig.key] || '';
        let valB = b[sortConfig.key] || '';

        if (sortConfig.key === 'addedAt') {
          if (!a[sortConfig.key] && b[sortConfig.key])
            return sortConfig.direction === 'asc' ? 1 : -1;
          if (a[sortConfig.key] && !b[sortConfig.key])
            return sortConfig.direction === 'asc' ? -1 : 1;
          if (!a[sortConfig.key] && !b[sortConfig.key]) return 0;

          valA = new Date(a[sortConfig.key]).getTime();
          valB = new Date(b[sortConfig.key]).getTime();

          if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
          if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }

        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return combinedRows;
  }, [dbRows, edits, newRows, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const executeDownload = useCallback(() => {
    const { selectedRegionIds, blockSize } = exportSettings;
    if (selectedRegionIds.length === 0) return;

    const filteredEmails = displayRows
      .filter((row) => selectedRegionIds.includes(row.regionId))
      .map((row) => row.email)
      .filter((email) => !!email);

    if (filteredEmails.length === 0) {
      setResultMessage('No emails found for the selected regions.');
      setResultSeverity('info');
      setShowResultAlert(true);
      return;
    }

    const size = parseInt(blockSize) || 50;
    let fileIndex = 1;

    for (let i = 0; i < filteredEmails.length; i += size) {
      const chunk = filteredEmails.slice(i, i + size);
      const content = chunk.join('; ');
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Upload_Mailing_List_${fileIndex}.txt`;
      link.click();
      URL.revokeObjectURL(url);
      fileIndex++;
    }
    setDownloadDialogOpen(false);
  }, [displayRows, exportSettings]);

  const handleAddNew = () => {
    const currentTotalRows = dbRows.length + newRows.length;
    if (currentTotalRows >= importLimit) return;

    const newId = `temp_${Date.now().toString()}`;
    setNewRows((prev) => [
      ...prev,
      { id: newId, userName: '', companyName: '', email: '', regionId: '', status: '' },
    ]);
    setActiveAction('EDIT_MODE');
    setFocusedRowId(newId);
  };

  const handleDeleteRequest = () => {
    if (selectedIds.length === 0) return;
    setShowDeleteAlert(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteAlert(false);
    setLoading(true);

    const localIdsToDelete = [];
    const dbIdsToDelete = [];

    selectedIds.forEach((id) => {
      if (newRows.some((r) => r.id === id)) localIdsToDelete.push(id);
      else dbIdsToDelete.push(id);
    });

    const totalDeleted = selectedIds.length;

    setNewRows((prev) => {
      const remainingRows = prev.filter((r) => !localIdsToDelete.includes(r.id));

      return remainingRows.map((row) => {
        if (!row.email || row.email.trim() === '') return { ...row, status: '' };

        const cleanEmail = row.email.toLowerCase().trim();

        // Checks the object property to trigger reliable React updates
        if (unsubscribedEmails[cleanEmail]) {
          return { ...row, status: unsubscribedEmails[cleanEmail] };
        }

        // Priority 2: Check against the real mailing list
        if (mailingListEmails.has(cleanEmail)) {
          return { ...row, status: 'Duplicate in Mailing List' };
        }

        // Priority 3: Check imported records
        const existsInDb = dbRows.some(
          (dbRow) =>
            dbRow.email &&
            dbRow.email.toLowerCase().trim() === cleanEmail &&
            !dbIdsToDelete.includes(dbRow.id),
        );

        const existsInOtherNewRows = remainingRows.some(
          (other) =>
            other.id !== row.id &&
            other.email &&
            other.email.toLowerCase().trim() === cleanEmail,
        );

        return {
          ...row,
          status: existsInDb || existsInOtherNewRows ? 'Duplicate Entry' : 'Ok',
        };
      });
    });

    let success = true;

    if (dbIdsToDelete.length > 0) {
      try {
        const batch = writeBatch(db);
        dbIdsToDelete.forEach((id) => {
          batch.delete(doc(db, 'uploadMailingList', id));
        });

        await batch.commit();
      } catch (error) {
        console.error('Deletion error:', error);
        setResultSeverity('error');
        setResultMessage('An error occurred while deleting the users.');
        setShowResultAlert(true);
        success = false;
      }
    }

    if (success && totalDeleted > 0) {
      setResultSeverity('success');
      setResultMessage(`${totalDeleted} user(s) removed from the import list.`);
      setShowResultAlert(true);
    }

    setLoading(false);
    setSelectedIds([]);
  };

  const processImport = async (rows) => {
    // Keeps the UI mounted and the file array processing without flashing loading state
    const currentTotalRows = dbRows.length + newRows.length;
    const availableSlots = Math.max(0, importLimit - currentTotalRows);

    let rowsToProcess = rows;
    let wasTruncated = false;
    let completelyBlocked = false;

    // Truncate instead of blocking if over the limit
    if (availableSlots === 0) {
      completelyBlocked = true;
      rowsToProcess = [];
    } else if (rows.length > availableSlots) {
      rowsToProcess = rows.slice(0, availableSlots);
      wasTruncated = true;
    }

    if (completelyBlocked) {
      setResultMessage(
        `Import blocked. The list already contains ${currentTotalRows} rows, reaching the maximum system limit of ${importLimit}.`,
      );
      setResultSeverity('error');
      setShowResultAlert(true);
      return;
    }

    try {
      setNewRows((prev) => {
        const addedRows = [];
        rowsToProcess.forEach((row, index) => {
          const cleanEmail = String(row.email).toLowerCase().trim();
          if (!cleanEmail) return;

          let newStatus = 'Ok';

          if (unsubscribedEmails[cleanEmail]) {
            newStatus = unsubscribedEmails[cleanEmail];
          } else if (mailingListEmails.has(cleanEmail)) {
            newStatus = 'Duplicate in Mailing List';
          } else if (
            dbRows.some((r) => r.email && r.email.toLowerCase().trim() === cleanEmail)
          ) {
            newStatus = 'Duplicate Entry';
          } else if (
            prev.some((r) => r.email && r.email.toLowerCase().trim() === cleanEmail) ||
            addedRows.some((r) => r.email === cleanEmail)
          ) {
            newStatus = 'Duplicate Entry';
          }

          addedRows.push({
            id: `temp_import_${Date.now()}_${index}`,
            userName: row.userName || '',
            companyName: row.companyName || '',
            email: cleanEmail,
            regionId: '',
            status: newStatus,
          });
        });
        return [...prev, ...addedRows];
      });

      setActiveAction('EDIT_MODE');

      // Alert the user that truncation occurred
      if (wasTruncated) {
        setResultMessage(
          `Import truncated. Adding ${rows.length} rows would exceed the system limit of ${importLimit}. Only ${availableSlots} rows were imported.`,
        );
        setResultSeverity('warning');
        setShowResultAlert(true);
      }
    } catch (error) {
      console.error('Import processing error:', error);
      setResultMessage('An error occurred while parsing the file.');
      setResultSeverity('error');
      setShowResultAlert(true);
    }
  };

  const executeSave = async () => {
    const batch = writeBatch(db);
    let savedCount = 0;
    let discardedCount = 0;

    newRows.forEach((row) => {
      if (row.email && row.email.trim() !== '') {
        if (row.status === 'Ok') {
          const cleanEmail = row.email.toLowerCase().trim();
          const existingRow = dbRows.find(
            (r) => r.email && r.email.toLowerCase().trim() === cleanEmail,
          );

          if (existingRow) {
            batch.update(doc(db, 'uploadMailingList', existingRow.id), {
              userName: row.userName || existingRow.userName || '',
              companyName: row.companyName || existingRow.companyName || '',
              addedAt: new Date().toISOString(),
              status: 'Ok',
            });
          } else {
            const newDocRef = doc(collection(db, 'uploadMailingList'));

            batch.set(newDocRef, {
              userName: row.userName || '',
              companyName: row.companyName || '',
              email: cleanEmail,
              addedAt: new Date().toISOString(),
              status: 'Ok',
            });
          }
          savedCount++;
        } else {
          discardedCount++;
        }
      }
    });

    let editSavedCount = 0;
    Object.keys(edits).forEach((id) => {
      const editsPayload = edits[id];
      batch.update(doc(db, 'uploadMailingList', id), editsPayload);
      editSavedCount++;
    });

    // FIX: Clear local state before committing to prevent duplicates caused by
    // Firestore's local latency compensation triggering onSnapshot mid-request.
    setNewRows([]);
    setEdits({});
    setActiveAction('VIEW');
    setShowOverwriteAlert(false);

    try {
      await batch.commit();
    } catch (error) {
      console.error('Error saving batch', error);
    }

    setResultSeverity('success');
    setResultMessage(
      `${savedCount + editSavedCount} users saved ${discardedCount} users discarded`,
    );
    setShowResultAlert(true);
  };

  const handleSave = () => {
    executeSave();
  };

  return {
    state: {
      displayRows,
      selectedIds,
      loading,
      focusedRowId,
      regions,
      downloadDialogOpen,
      exportSettings,
      sortConfig,
      importLimit,
    },
    flags: {
      isDirty: newRows.length > 0 || Object.keys(edits).length > 0,
      canDelete: selectedIds.length > 0,
      canSave: newRows.length > 0 || Object.keys(edits).length > 0,
      canAddNew: dbRows.length + newRows.length < importLimit, // UI Constraint
    },
    alerts: {
      showDelete: showDeleteAlert,
      setShowDelete: setShowDeleteAlert,
      showUnsaved: showUnsavedAlert,
      setShowUnsaved: setShowUnsavedAlert,
      showResultAlert,
      setShowResultAlert,
      resultMessage,
      setResultMessage,
      resultSeverity,
      setResultSeverity,
      showOverwriteAlert,
      setShowOverwriteAlert,
    },
    actions: {
      handleAddNew,
      handleDownload: () => setDownloadDialogOpen(true),
      setDownloadDialogOpen,
      setExportSettings,
      executeDownload,
      processImport,
      handleDeleteRequest,
      handleConfirmDelete,
      requestSort,
      handleSave,
      executeSave,
      handleCheckboxChange: (id) =>
        setSelectedIds((prev) =>
          prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
        ),
      handleSelectAll: (checked) => {
        if (checked) {
          setSelectedIds(displayRows.map((row) => row.id));
        } else {
          setSelectedIds([]);
        }
      },
      handleFieldChange: (id, field, val) => {
        if (activeAction === 'VIEW') setActiveAction('EDIT_MODE');

        if (newRows.some((r) => r.id === id)) {
          setNewRows((prev) => {
            const updatedRows = prev.map((r) =>
              r.id === id ? { ...r, [field]: val } : r,
            );

            if (field === 'email') {
              return updatedRows.map((row) => {
                if (!row.email || row.email.trim() === '') {
                  return { ...row, status: '' };
                }

                const cleanEmail = row.email.toLowerCase().trim();

                if (unsubscribedEmails[cleanEmail]) {
                  return { ...row, status: unsubscribedEmails[cleanEmail] };
                }

                // Priority 2: Check the real mailing list
                if (mailingListEmails.has(cleanEmail)) {
                  return { ...row, status: 'Duplicate in Mailing List' };
                }

                // Priority 3: Check the existing import list
                const existsInDb = dbRows.some(
                  (dbRow) =>
                    dbRow.email && dbRow.email.toLowerCase().trim() === cleanEmail,
                );

                const existsInOtherNewRows = updatedRows.some(
                  (otherRow) =>
                    otherRow.id !== row.id &&
                    otherRow.email &&
                    otherRow.email.toLowerCase().trim() === cleanEmail,
                );

                return {
                  ...row,
                  status: existsInDb || existsInOtherNewRows ? 'Duplicate Entry' : 'Ok',
                };
              });
            }

            return updatedRows;
          });
        } else {
          setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: val } }));
        }
      },
      setFocusedRowId,
      resetInterface: () => {
        setNewRows([]);
        setEdits({});
        setSelectedIds([]);
        setShowUnsavedAlert(false);
        setActiveAction('VIEW');
      },
    },
  };
}
